import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './PeopleSliderItem.module.scss';
import { setupTransitionInTimeline, scaleImage } from './PeopleSliderItem.transitions';
import type { PeopleSliderItemProps, PeopleSliderItemRef } from './PeopleSliderItem.data';
import { useScrollTransition } from 'util/react-transition-component';
import Image from '../../../atoms/image/Image';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import type { ImageRef } from '../../../atoms/image/Image.data';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';
import Link from 'next/link';
import { useCanPlayWebmContext } from '../../../../context/canPlayWebmContext';
import { getLink } from 'util/url.utils';
import gsap from 'gsap';
import GrowingArrowButton from 'components/atoms/growing-arrow-button/GrowingArrowButton';
import type { GrowingArrowButtonRef } from 'components/atoms/growing-arrow-button/GrowingArrowButton.data';

export default function PeopleSliderItem({
  includeImage,
  blok,
  transitionInDelay,
  figCaptionMaxHeight,
  viewTextLabel,
  ref,
  ...props
}: PeopleSliderItemProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<PeopleSliderItemRef | null>)?.current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const imageRef = useRef<ImageRef>(null);
  const labelRef = useRef<HeadingRef>(null);
  const imageHolderRef = useRef<HTMLDivElement>(null);
  const jobTitleRef = useRef<TextRef>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const textContentTimelineRef = useRef<gsap.core.Timeline | null>(null);

  const buttonRef = useRef<GrowingArrowButtonRef>(null);

  const { canPlayWebm } = useCanPlayWebmContext();

  const link = getLink(blok.link);

  const { name, job_title } = blok;

  useImperativeHandle(
    ref,
    () => ({
      imageRef,
      elementRef,
    }),
    [imageRef, elementRef],
  );

  useScrollTransition({
    scroll: {
      transitionInDelay,
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        imageRef,
        labelRef,
        jobTitleRef,
        imageHolderRef,
        imageContainerRef,
        // linkRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const [isHover, setIsHover] = useState(false);

  useEffect(() => {
    scaleImage(imageRef, isHover);
  }, [isHover]);

  useEffect(() => {
    const textContentElement = textContentRef.current;

    if (!textContentElement) return;

    textContentTimelineRef.current = gsap
      .timeline({ paused: true })
      .to(textContentElement, { autoAlpha: 0, duration: 0.3, ease: 'power2.inOut' })
      .to({}, { duration: 0.4 });

    return () => {
      textContentTimelineRef.current?.kill();
      textContentTimelineRef.current = null;
    };
  }, []);

  const onMouseEnter = useCallback(() => {
    setIsHover(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setTimeout(() => {
      setIsHover(false);
    }, 0);
  }, []);

  const onContainerMouseLeave = useCallback(() => {
    buttonRef.current?.reverse();
    textContentTimelineRef.current?.reverse();
  }, []);

  const onContainerMouseEnter = useCallback(() => {
    buttonRef.current?.play();
    textContentTimelineRef.current?.play();
  }, []);

  const Wrapper = link ? Link : 'div';

  return (
    <Wrapper
      // @ts-ignore
      href={link}
      className={classNames(props.className, styles.peopleSliderItem, { [styles.link]: link })}
      // @ts-ignore
      ref={elementRef}
      onMouseLeave={onContainerMouseLeave}
      onMouseEnter={onContainerMouseEnter}
    >
      {includeImage ? (
        <article className={classNames(styles.card)}>
          {blok.image.filename && (
            <div
              ref={imageHolderRef}
              className={classNames(styles.imageContainer, { [styles.pointer]: isHover })}
              onMouseEnter={link ? onMouseEnter : undefined}
              onMouseLeave={link ? onMouseLeave : undefined}
            >
              <div ref={imageContainerRef} className={classNames(styles.imageHolder)}>
                {blok?.animated_image?.filename && canPlayWebm ? (
                  <video
                    src={blok.animated_image.filename}
                    autoPlay
                    muted
                    controls={false}
                    playsInline
                    loop
                    className={classNames(styles.animatedImage)}
                  />
                ) : (
                  <Image
                    ref={imageRef}
                    src={blok.image.filename}
                    srcDesktop={blok.image.filename}
                    className={classNames(styles.image)}
                    layout="responsive"
                    objectFit="cover"
                    alt={blok.image.alt}
                  />
                )}
              </div>
            </div>
          )}

          <div
            id={'identityDetails'}
            className={styles.identityDetails}
            style={figCaptionMaxHeight ? { height: figCaptionMaxHeight } : undefined}
          >
            <div className={styles.textContentWrapper}>
              <div ref={textContentRef} className={styles.textContent}>
                {name && (
                  <Text
                    ref={jobTitleRef}
                    transitionInType="none"
                    as="p"
                    size="body-standard"
                    className={classNames(styles.client)}
                  >
                    {name}
                  </Text>
                )}
                {job_title && (
                  <Heading
                    ref={labelRef}
                    transitionInType="none"
                    as="h4"
                    size="label-large"
                    className={styles.title}
                  >
                    {job_title}
                  </Heading>
                )}
              </div>
            </div>

            {link && (
              <GrowingArrowButton
                ref={buttonRef}
                label={viewTextLabel ?? ''}
                className={styles.button}
              />
            )}
          </div>
        </article>
      ) : (
        <article className={classNames(styles.card, styles.noImageCard)}>
          <div
            id={'identityDetails'}
            className={styles.identityDetails}
            style={figCaptionMaxHeight ? { height: figCaptionMaxHeight } : undefined}
          >
            <div className={styles.textContentWrapper}>
              <div ref={textContentRef} className={styles.textContent}>
                {name && (
                  <Text
                    ref={jobTitleRef}
                    transitionInType="none"
                    as="p"
                    size="body-standard"
                    className={classNames(styles.client)}
                  >
                    {name}
                  </Text>
                )}
                {job_title && (
                  <Heading
                    ref={labelRef}
                    transitionInType="none"
                    as="h4"
                    size="label-large"
                    className={styles.title}
                  >
                    {job_title}
                  </Heading>
                )}
              </div>
            </div>

            {link && (
              <GrowingArrowButton
                ref={buttonRef}
                label={viewTextLabel ?? ''}
                className={styles.button}
              />
            )}
          </div>
        </article>
      )}
    </Wrapper>
  );
}
